/**
 * AdminPanel — live-ops workbench for uploading assets, defining custom
 * items, and swapping page banners without a redeploy.
 *
 * Access is enforced server-side; we still gate the nav entry client-side
 * with `snap.is_admin` so non-admins never see the link.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Image as ImageIcon, Package, Layers, Trash2, Copy,
  Check, X, Plus, RefreshCw, Search, Lock, Sparkles,
} from "lucide-react";

import { api, API, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useGame } from "@/context/GameContext";

const CATEGORIES = [
  "weapon", "armour", "food", "raw_fish", "ore", "bar", "logs",
  "gem", "material", "potion", "rune", "misc",
];

const SLOTS = [
  "", "weapon", "head", "cape", "neck", "ammo",
  "body", "shield", "legs", "gloves", "boots", "ring",
];

const STAT_KEYS = ["atk", "str", "def", "speed", "heal"];

const PAGE_SLUGS = [
  "landing", "profile", "map", "worldbosses", "grand_exchange",
  "housing", "quests", "events", "achievements", "museum",
];

// -----------------------------------------------------------------------

function useAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/uploads");
      setAssets(data.assets || []);
    } catch (e) {
      toast.error("Failed to load assets", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { assets, loading, refresh, setAssets };
}

function useItems() {
  const [items, setItems] = useState([]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/items");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load items", { description: formatApiError(e) });
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { items, refresh, setItems };
}

function useBanners() {
  const [banners, setBanners] = useState([]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/banners");
      setBanners(data.banners || []);
    } catch (e) {
      toast.error("Failed to load banners", { description: formatApiError(e) });
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { banners, refresh };
}

function useSkillMeta() {
  const [skills, setSkills] = useState([]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/skills");
      setSkills(data.skills || []);
    } catch (e) {
      toast.error("Failed to load skill metadata", { description: formatApiError(e) });
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { skills, refresh };
}

function useSkillActions(skill) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    if (!skill) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/actions?skill=${encodeURIComponent(skill)}`);
      setActions(data.actions || []);
    } catch (e) {
      toast.error("Failed to load actions", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [skill]);
  useEffect(() => { refresh(); }, [refresh]);
  return { actions, loading, refresh };
}

// Convert the API-relative /api/files/{id} to a full-origin URL for <img>.
function absoluteUrl(pathOrNull) {
  if (!pathOrNull) return null;
  if (/^https?:/i.test(pathOrNull)) return pathOrNull;
  return `${API.replace(/\/api$/, "")}${pathOrNull}`;
}

// -----------------------------------------------------------------------

function UploadDropzone({ onUploaded }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Only image files can be uploaded here.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (label.trim()) fd.append("label", label.trim());
      const { data } = await api.post("/api/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Asset uploaded", { description: data.asset.filename });
      setLabel("");
      onUploaded?.(data.asset);
    } catch (e) {
      toast.error("Upload failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  }, [label, onUploaded]);

  return (
    <div className="panel-inset p-4 space-y-3" data-testid="admin-upload-dropzone">
      <div className="flex items-center gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Optional label (e.g. Dragon sword icon)"
          className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
          data-testid="admin-upload-label"
        />
      </div>
      <label
        htmlFor="admin-file-input"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer?.files?.[0];
          if (file) doUpload(file);
        }}
        className={
          "flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-8 cursor-pointer transition-colors " +
          (dragging ? "border-amber-500 bg-amber-950/20" : "border-stone-700 hover:border-amber-700")
        }
        data-testid="admin-upload-drop"
      >
        <Upload size={28} strokeWidth={1.4} className={busy ? "animate-pulse text-amber-400" : "text-stone-500"} />
        <div className="text-sm font-body text-stone-300">
          {busy ? "Uploading…" : "Drop an image or click to browse"}
        </div>
        <div className="text-[11px] font-body text-stone-500">
          PNG / JPG / WEBP / GIF / SVG · up to 8 MB
        </div>
        <input
          id="admin-file-input"
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
          data-testid="admin-upload-input"
        />
      </label>
    </div>
  );
}

function AssetTile({ asset, onDelete, onPick }) {
  const [copied, setCopied] = useState(false);
  const url = absoluteUrl(asset.url);
  return (
    <div
      className="panel-inset p-2 space-y-2 group relative"
      data-testid={`admin-asset-${asset.id}`}
    >
      <div className="aspect-square bg-stone-950 rounded overflow-hidden flex items-center justify-center">
        {url ? (
          <img src={url} alt={asset.filename} className="w-full h-full object-contain" />
        ) : (
          <ImageIcon size={28} className="text-stone-600" />
        )}
      </div>
      <div className="text-[11px] font-body text-stone-400 truncate" title={asset.filename}>
        {asset.label || asset.filename}
      </div>
      <div className="flex items-center gap-1">
        {onPick && (
          <button
            onClick={() => onPick(asset)}
            className="flex-1 text-[11px] font-body px-2 py-1 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
            data-testid={`admin-asset-pick-${asset.id}`}
          >
            Use
          </button>
        )}
        <button
          onClick={() => {
            navigator.clipboard?.writeText(asset.id);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
          title="Copy asset id"
          className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
          data-testid={`admin-asset-copy-${asset.id}`}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
        <button
          onClick={() => onDelete?.(asset)}
          title="Delete"
          className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-500 hover:text-red-400 hover:border-red-900 transition-colors"
          data-testid={`admin-asset-delete-${asset.id}`}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function AssetsTab() {
  const { assets, loading, refresh } = useAssets();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return assets;
    return assets.filter((a) =>
      (a.filename || "").toLowerCase().includes(needle) ||
      (a.label || "").toLowerCase().includes(needle),
    );
  }, [assets, q]);

  const onDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.label || asset.filename}"?`)) return;
    try {
      await api.delete(`/api/admin/uploads/${asset.id}`);
      toast.success("Asset removed");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-assets-tab">
      <UploadDropzone onUploaded={refresh} />

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1 max-w-sm">
          <Search size={14} className="text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search assets…"
            className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-assets-search"
          />
        </div>
        <button
          onClick={refresh}
          className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 flex items-center gap-1 transition-colors"
          data-testid="admin-assets-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <div className="text-[11px] font-numeric text-stone-500 uppercase tracking-widest">
          {filtered.length}/{assets.length}
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
        {filtered.map((a) => (
          <AssetTile key={a.id} asset={a} onDelete={onDelete} />
        ))}
        {!filtered.length && (
          <div className="col-span-full text-center text-stone-500 italic py-10 font-body">
            No assets yet. Drop a file above to get started.
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function AssetPickerModal({ open, onClose, onPick }) {
  const { assets, loading, refresh } = useAssets();
  const [q, setQ] = useState("");

  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  if (!open) return null;

  const filtered = assets.filter((a) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (a.filename || "").toLowerCase().includes(needle) ||
      (a.label || "").toLowerCase().includes(needle);
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-asset-picker"
    >
      <div
        className="panel max-w-4xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div>
            <h3 className="font-display text-lg heading-gilded">Pick an asset</h3>
            <p className="text-[11px] font-body text-stone-500">Selecting will link this asset to the current form.</p>
          </div>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-amber-300 transition-colors"
            data-testid="admin-asset-picker-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-stone-800 flex items-center gap-2">
          <Search size={14} className="text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
            autoFocus
          />
          <button
            onClick={refresh}
            className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 flex items-center gap-1"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {filtered.map((a) => (
              <AssetTile key={a.id} asset={a} onPick={(asset) => { onPick(asset); onClose(); }} />
            ))}
            {!filtered.length && (
              <div className="col-span-full text-center text-stone-500 italic py-10 font-body">
                No matching assets. Upload one from the Assets tab first.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

const BLANK_ITEM = {
  item_id: "", name: "", cat: "misc", slot: "", description: "",
  icon_asset_id: null, stats: {}, published: false,
};

function ItemForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial || BLANK_ITEM);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickedIcon, setPickedIcon] = useState(null);

  useEffect(() => { setForm(initial || BLANK_ITEM); setPickedIcon(null); }, [initial]);

  const iconPreview = pickedIcon
    ? absoluteUrl(pickedIcon.url)
    : (initial?.icon_url ? absoluteUrl(initial.icon_url) : null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) => setForm((f) => ({
    ...f,
    stats: { ...(f.stats || {}), [k]: v === "" ? undefined : Number(v) },
  }));

  const save = async () => {
    if (!form.item_id.trim() || !form.name.trim()) {
      toast.error("item_id and name are required.");
      return;
    }
    setSaving(true);
    try {
      const cleanStats = Object.fromEntries(
        Object.entries(form.stats || {}).filter(([, v]) => Number.isFinite(v) && v !== 0),
      );
      const payload = {
        ...form,
        slot: form.slot || null,
        stats: cleanStats,
      };
      const { data } = await api.post("/api/admin/items", payload);
      toast.success(`Item saved: ${data.item.name}`);
      onSaved?.(data.item);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel-inset p-4 space-y-3" data-testid="admin-item-form">
      {initial?.source === "builtin" && (
        <div className="text-[11px] font-body text-sky-300 bg-sky-950/30 border border-sky-800/60 rounded px-3 py-2">
          Editing a built-in item — saving creates a custom override at the same id. Undo by deleting the custom row.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Item id</span>
          <input
            value={form.item_id}
            onChange={(e) => setField("item_id", e.target.value)}
            placeholder="e.g. dragon_scimitar"
            disabled={!!initial}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none disabled:opacity-60"
            data-testid="admin-item-id"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Display name</span>
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Dragon scimitar"
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-item-name"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Category</span>
          <select
            value={form.cat}
            onChange={(e) => setField("cat", e.target.value)}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-item-cat"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Equipment slot (optional)</span>
          <select
            value={form.slot || ""}
            onChange={(e) => setField("slot", e.target.value)}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-item-slot"
          >
            {SLOTS.map((s) => <option key={s || "_"} value={s}>{s || "— none —"}</option>)}
          </select>
        </label>
      </div>

      <label className="space-y-1 block">
        <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Description</span>
        <textarea
          value={form.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="Flavour text shown on hover / inspect."
          rows={2}
          className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
          data-testid="admin-item-desc"
        />
      </label>

      <div className="grid grid-cols-5 gap-2">
        {STAT_KEYS.map((k) => (
          <label key={k} className="space-y-1">
            <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">{k}</span>
            <input
              type="number"
              value={form.stats?.[k] ?? ""}
              onChange={(e) => setStat(k, e.target.value)}
              className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-numeric text-amber-300 focus:border-amber-600 outline-none"
              data-testid={`admin-item-stat-${k}`}
            />
          </label>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="w-16 h-16 bg-stone-950 border border-stone-700 rounded overflow-hidden flex items-center justify-center">
          {iconPreview ? (
            <img src={iconPreview} alt="icon" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={22} className="text-stone-600" />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <button
            onClick={() => setPickerOpen(true)}
            className="text-xs font-body px-3 py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors self-start"
            data-testid="admin-item-pick-icon"
          >
            {form.icon_asset_id ? "Change icon" : "Choose icon"}
          </button>
          {form.icon_asset_id && (
            <button
              onClick={() => { setField("icon_asset_id", null); setPickedIcon(null); }}
              className="text-[11px] font-body text-stone-500 hover:text-red-400 self-start"
            >
              Clear icon
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs font-body text-stone-300">
          <input
            type="checkbox"
            checked={!!form.published}
            onChange={(e) => setField("published", e.target.checked)}
            data-testid="admin-item-published"
          />
          Publish live
        </label>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-700 hover:bg-amber-600 text-stone-950 font-body"
          data-testid="admin-item-save"
        >
          {saving ? "Saving…" : (initial ? "Update item" : "Create item")}
        </Button>
      </div>

      <AssetPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(a) => { setField("icon_asset_id", a.id); setPickedIcon(a); }}
      />
    </div>
  );
}

function ItemsTab() {
  const { items, refresh } = useItems();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | custom | builtin

  const togglePublish = async (item) => {
    try {
      await api.post(`/api/admin/items/${item.id}/publish`, { published: !item.published });
      toast.success(`${item.name} ${!item.published ? "published live" : "hidden from live"}`);
      refresh();
    } catch (e) {
      toast.error("Publish toggle failed", { description: formatApiError(e) });
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`/api/admin/items/${item.id}`);
      toast.success("Item removed");
      refresh();
      if (editing?.id === item.id) setEditing(null);
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  const filtered = items.filter((it) => {
    if (filter === "custom" && it.source !== "custom") return false;
    if (filter === "builtin" && it.source !== "builtin") return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (
      it.name.toLowerCase().includes(needle) ||
      it.id.toLowerCase().includes(needle) ||
      (it.cat || "").toLowerCase().includes(needle)
    );
  });

  const customCount  = items.filter((i) => i.source === "custom").length;
  const builtinCount = items.filter((i) => i.source === "builtin").length;

  return (
    <div className="space-y-4" data-testid="admin-items-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg heading-gilded">Items catalogue</h3>
          <p className="text-[11px] font-body text-stone-500">
            {customCount} custom · {builtinCount} built-in. Custom items can be edited & published; built-ins are always live.
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-1 text-xs font-body px-3 py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
          data-testid="admin-item-new"
        >
          <Plus size={14} /> New item
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, id, category…"
            className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-items-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all",     label: `All (${items.length})` },
            { id: "custom",  label: `Custom (${customCount})` },
            { id: "builtin", label: `Built-in (${builtinCount})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={
                "text-[11px] font-body px-2 py-1 rounded border transition-colors " +
                (filter === f.id
                  ? "border-amber-700 bg-amber-950/40 text-amber-200"
                  : "border-stone-700 text-stone-400 hover:text-amber-200 hover:border-amber-800")
              }
              data-testid={`admin-items-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {(creating || editing) && (
        <ItemForm
          initial={editing}
          onSaved={() => { refresh(); setCreating(false); setEditing(null); }}
        />
      )}

      <div className="grid gap-2">
        {items.map((it) => {
          const isBuiltin = it.source === "builtin";
          return (
          <div
            key={it.id}
            className="panel-inset p-3 flex items-center gap-3"
            data-testid={`admin-item-row-${it.id}`}
          >
            <div className="w-12 h-12 bg-stone-950 border border-stone-800 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
              {it.icon_url ? (
                <img src={absoluteUrl(it.icon_url)} alt={it.name} className="w-full h-full object-contain" />
              ) : (
                <ImageIcon size={20} className="text-stone-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-body text-sm text-stone-200 truncate flex items-center gap-2">
                {it.name}
                <span className="text-stone-600 font-numeric text-[11px]">/{it.id}/</span>
                {isBuiltin && (
                  <span className="text-[9px] font-numeric uppercase tracking-widest text-sky-300 border border-sky-800/60 rounded px-1.5 py-0.5">
                    Built-in
                  </span>
                )}
              </div>
              <div className="text-[11px] font-body text-stone-500 truncate">
                {it.cat}{it.slot ? ` · ${it.slot}` : ""} · {Object.entries(it.stats || {}).map(([k, v]) => `${k}+${v}`).join(" ") || "no stats"}
              </div>
            </div>
            {!isBuiltin && (
              <button
                onClick={() => togglePublish(it)}
                className={
                  "text-[11px] font-numeric uppercase tracking-widest px-2 py-1 rounded border transition-colors " +
                  (it.published
                    ? "text-emerald-300 border-emerald-800 hover:bg-emerald-950/30"
                    : "text-stone-500 border-stone-700 hover:text-amber-300 hover:border-amber-800")
                }
                data-testid={`admin-item-publish-${it.id}`}
              >
                {it.published ? "Live" : "Draft"}
              </button>
            )}
            {isBuiltin && (
              <span className="text-[11px] font-numeric uppercase tracking-widest text-emerald-300 border border-emerald-800 px-2 py-1 rounded">
                Live
              </span>
            )}
            <button
              onClick={() => { setEditing({ ...it, published: true }); setCreating(false); }}
              className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
              data-testid={`admin-item-edit-${it.id}`}
              title={isBuiltin ? "Editing a built-in creates a custom override" : "Edit item"}
            >
              Edit
            </button>
            {!isBuiltin && (
              <button
                onClick={() => remove(it)}
                className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-500 hover:text-red-400 hover:border-red-900 transition-colors"
                data-testid={`admin-item-delete-${it.id}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
          );
        })}
        {!filtered.length && (
          <div className="text-center text-stone-500 italic py-10 font-body panel-inset">
            No items match your search. Try clearing the filter.
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function BannerForm({ initial, onSaved }) {
  const BLANK = { page: PAGE_SLUGS[0], title: "", subtitle: "", image_asset_id: null, is_active: true };
  const [form, setForm] = useState(initial || BLANK);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedImage, setPickedImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial || BLANK); setPickedImage(null); }, [initial]);

  const preview = pickedImage
    ? absoluteUrl(pickedImage.url)
    : (initial?.image_url ? absoluteUrl(initial.image_url) : null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.page) { toast.error("Pick a page slug."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/api/admin/banners", form);
      toast.success(`Banner saved for ${data.banner.page}`);
      onSaved?.(data.banner);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel-inset p-4 space-y-3" data-testid="admin-banner-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Page slug</span>
          <input
            list="banner-pages"
            value={form.page}
            onChange={(e) => setField("page", e.target.value.toLowerCase().replace(/\s+/g, "_"))}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-banner-page"
          />
          <datalist id="banner-pages">
            {PAGE_SLUGS.map((p) => <option key={p} value={p} />)}
          </datalist>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Title</span>
          <input
            value={form.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Optional headline"
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-banner-title"
          />
        </label>
      </div>
      <label className="space-y-1 block">
        <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Subtitle</span>
        <textarea
          rows={2}
          value={form.subtitle || ""}
          onChange={(e) => setField("subtitle", e.target.value)}
          placeholder="Sub-copy shown under the banner title."
          className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
          data-testid="admin-banner-subtitle"
        />
      </label>
      <div className="flex items-center gap-3">
        <div className="w-32 h-16 bg-stone-950 border border-stone-700 rounded overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="banner" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={22} className="text-stone-600" />
          )}
        </div>
        <div className="flex-1">
          <button
            onClick={() => setPickerOpen(true)}
            className="text-xs font-body px-3 py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
            data-testid="admin-banner-pick-image"
          >
            {form.image_asset_id ? "Change image" : "Choose image"}
          </button>
        </div>
        <label className="flex items-center gap-2 text-xs font-body text-stone-300">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => setField("is_active", e.target.checked)}
            data-testid="admin-banner-active"
          />
          Active
        </label>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-700 hover:bg-amber-600 text-stone-950 font-body"
          data-testid="admin-banner-save"
        >
          {saving ? "Saving…" : "Save banner"}
        </Button>
      </div>
      <AssetPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(a) => { setField("image_asset_id", a.id); setPickedImage(a); }}
      />
    </div>
  );
}

function BannersTab() {
  const { banners, refresh } = useBanners();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const remove = async (b) => {
    if (!window.confirm(`Delete banner for "${b.page}"?`)) return;
    try {
      await api.delete(`/api/admin/banners/${b.page}`);
      toast.success("Banner removed");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-banners-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg heading-gilded">Page banners</h3>
          <p className="text-[11px] font-body text-stone-500">
            One banner per page slug. Any page can call <span className="font-numeric">/api/content/banners/&lt;slug&gt;</span>.
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-1 text-xs font-body px-3 py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
          data-testid="admin-banner-new"
        >
          <Plus size={14} /> New banner
        </button>
      </div>

      {(creating || editing) && (
        <BannerForm
          initial={editing}
          onSaved={() => { refresh(); setCreating(false); setEditing(null); }}
        />
      )}

      <div className="grid gap-2">
        {banners.map((b) => (
          <div
            key={b.id}
            className="panel-inset p-3 flex items-center gap-3"
            data-testid={`admin-banner-row-${b.page}`}
          >
            <div className="w-24 h-14 bg-stone-950 border border-stone-800 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
              {b.image_url ? (
                <img src={absoluteUrl(b.image_url)} alt={b.title || b.page} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={18} className="text-stone-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-body text-sm text-stone-200 truncate">
                {b.title || <span className="text-stone-500 italic">no title</span>}
                <span className="text-stone-600 font-numeric text-[11px] ml-2">/{b.page}/</span>
              </div>
              {b.subtitle && (
                <div className="text-[11px] font-body text-stone-500 truncate">{b.subtitle}</div>
              )}
            </div>
            <span
              className={
                "text-[10px] font-numeric uppercase tracking-widest px-2 py-1 rounded border " +
                (b.is_active
                  ? "text-emerald-300 border-emerald-800"
                  : "text-stone-500 border-stone-700")
              }
            >
              {b.is_active ? "Active" : "Hidden"}
            </span>
            <button
              onClick={() => { setEditing(b); setCreating(false); }}
              className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
              data-testid={`admin-banner-edit-${b.page}`}
            >
              Edit
            </button>
            <button
              onClick={() => remove(b)}
              className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-500 hover:text-red-400 hover:border-red-900 transition-colors"
              data-testid={`admin-banner-delete-${b.page}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {!banners.length && (
          <div className="text-center text-stone-500 italic py-10 font-body panel-inset">
            No banners yet. Add one to decorate a page.
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

const CATEGORY_LABELS = ["Gathering", "Artisan", "Support", "Combat"];

function SkillForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState({
    skill:          initial.skill,
    label:          initial.label || "",
    tagline:        initial.tagline || "",
    category_label: initial.category_label || "",
    icon_asset_id:  initial.icon_asset_id || null,
    hero_asset_id:  initial.hero_asset_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);   // "icon" | "hero" | null
  const [pickedIcon, setPickedIcon] = useState(null);
  const [pickedHero, setPickedHero] = useState(null);

  useEffect(() => {
    setForm({
      skill:          initial.skill,
      label:          initial.label || "",
      tagline:        initial.tagline || "",
      category_label: initial.category_label || "",
      icon_asset_id:  initial.icon_asset_id || null,
      hero_asset_id:  initial.hero_asset_id || null,
    });
    setPickedIcon(null);
    setPickedHero(null);
  }, [initial]);

  const iconPreview = pickedIcon
    ? absoluteUrl(pickedIcon.url)
    : (initial.icon_url ? absoluteUrl(initial.icon_url) : null);
  const heroPreview = pickedHero
    ? absoluteUrl(pickedHero.url)
    : (initial.hero_url ? absoluteUrl(initial.hero_url) : null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/api/admin/skills", form);
      toast.success(`${data.skill.label} saved`);
      onSaved?.(data.skill);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${initial.skill} to default label/tagline/artwork?`)) return;
    try {
      await api.delete(`/api/admin/skills/${initial.skill}`);
      toast.success("Reverted to defaults");
      onSaved?.({ ...initial, has_override: false });
    } catch (e) {
      toast.error("Reset failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="panel-inset p-4 space-y-3" data-testid={`admin-skill-form-${form.skill}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-numeric text-amber-500">Editing skill</div>
          <div className="font-display text-lg heading-gilded">{form.skill}</div>
        </div>
        <button
          onClick={onClose}
          className="text-stone-500 hover:text-red-300"
          title="Close editor"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Display label</span>
          <input
            value={form.label}
            onChange={(e) => setField("label", e.target.value)}
            placeholder={initial.label || "e.g. Woodcutting"}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-skill-label"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Category label</span>
          <input
            list="skill-categories"
            value={form.category_label}
            onChange={(e) => setField("category_label", e.target.value)}
            placeholder={initial.category_label || "Gathering"}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="admin-skill-category"
          />
          <datalist id="skill-categories">
            {CATEGORY_LABELS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>
      </div>
      <label className="space-y-1 block">
        <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Tagline</span>
        <textarea
          rows={2}
          value={form.tagline}
          onChange={(e) => setField("tagline", e.target.value)}
          placeholder={initial.tagline || "The forest bends to a patient axe."}
          className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
          data-testid="admin-skill-tagline"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 bg-stone-950 border border-stone-700 rounded overflow-hidden flex items-center justify-center shrink-0">
            {iconPreview ? (
              <img src={iconPreview} alt="icon" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon size={20} className="text-stone-600" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Skill icon</div>
            <button
              onClick={() => setPickerFor("icon")}
              className="text-xs font-body px-2.5 py-1 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors mt-0.5"
              data-testid="admin-skill-pick-icon"
            >
              {form.icon_asset_id ? "Change icon" : "Choose icon"}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 h-16 bg-stone-950 border border-stone-700 rounded overflow-hidden flex items-center justify-center shrink-0">
            {heroPreview ? (
              <img src={heroPreview} alt="banner" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon size={22} className="text-stone-600" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">Hero banner</div>
            <button
              onClick={() => setPickerFor("hero")}
              className="text-xs font-body px-2.5 py-1 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors mt-0.5"
              data-testid="admin-skill-pick-hero"
            >
              {form.hero_asset_id ? "Change banner" : "Choose banner"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-stone-800/60">
        <button
          onClick={reset}
          disabled={!initial.has_override}
          className="text-xs font-body px-3 py-1.5 rounded border border-stone-700 text-stone-400 hover:text-red-300 hover:border-red-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="admin-skill-reset"
        >
          Reset to defaults
        </button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-700 hover:bg-amber-600 text-stone-950 font-body"
          data-testid="admin-skill-save"
        >
          {saving ? "Saving…" : "Save skill"}
        </Button>
      </div>

      <AssetPickerModal
        open={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        onPick={(a) => {
          if (pickerFor === "icon") {
            setField("icon_asset_id", a.id);
            setPickedIcon(a);
          } else if (pickerFor === "hero") {
            setField("hero_asset_id", a.id);
            setPickedHero(a);
          }
        }}
      />

      {/* Actions editor — new/existing actions for this skill */}
      <SkillActionsSection skill={initial.skill} />
    </div>
  );
}

// ---- Actions inside a skill --------------------------------------------

function newActionTemplate(skill) {
  return {
    action_id:       "",
    name:            "",
    skill,
    level:           1,
    duration_ticks:  5,
    xp:              10,
    yields:          [{ item_id: "", qty: 1, probability: 1.0 }],
    cost:            [],
    burn_stop_level: null,
    is_custom:       true,
    is_new:          true,
  };
}

function ActionForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (arr, i, patch) => arr.map((row, idx) => idx === i ? { ...row, ...patch } : row);

  const save = async () => {
    if (!form.action_id || !/^[a-z0-9_]+$/.test(form.action_id)) {
      toast.error("Action id must be lowercase letters, numbers and underscores only.");
      return;
    }
    if (!form.name?.trim()) {
      toast.error("Give the action a display name.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        yields: (form.yields || []).filter((y) => y.item_id.trim()).map((y) => ({
          item_id: y.item_id.trim(), qty: Number(y.qty) || 1, probability: Number(y.probability) || 1.0,
        })),
        cost: (form.cost || []).filter((c) => c.item_id.trim()).map((c) => ({
          item_id: c.item_id.trim(), qty: Number(c.qty) || 1,
        })),
        burn_stop_level: form.burn_stop_level || null,
      };
      const { data } = await api.post("/api/admin/actions", payload);
      toast.success(`Action "${data.action.name}" saved`);
      onSaved?.(data.action);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel-inset p-3 space-y-3 border-amber-800/40" data-testid={`admin-action-form-${form.action_id || "new"}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest font-numeric text-amber-500">
          {form.is_new ? "New action" : "Edit action"}
        </span>
        <span className="text-stone-600 font-numeric text-[10px]">/{form.skill}/</span>
        <div className="flex-1" />
        <button onClick={onCancel} className="text-stone-500 hover:text-red-300" title="Cancel">
          <X size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="space-y-1 col-span-2">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">Action id</span>
          <input
            value={form.action_id}
            onChange={(e) => setField("action_id", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
            placeholder="chop_ancient"
            disabled={!form.is_new}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-numeric focus:border-amber-600 outline-none disabled:opacity-70"
            data-testid="admin-action-id"
          />
        </label>
        <label className="space-y-1 col-span-2">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">Display name</span>
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Chop ancient tree"
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-body focus:border-amber-600 outline-none"
            data-testid="admin-action-name"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">Unlock lv.</span>
          <input
            type="number" min={1} max={99}
            value={form.level}
            onChange={(e) => setField("level", Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-numeric focus:border-amber-600 outline-none"
            data-testid="admin-action-level"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">Ticks / cycle</span>
          <input
            type="number" min={1} max={200}
            value={form.duration_ticks}
            onChange={(e) => setField("duration_ticks", Math.max(1, Number(e.target.value) || 5))}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-numeric focus:border-amber-600 outline-none"
            data-testid="admin-action-ticks"
          />
          <span className="text-[9px] font-numeric text-stone-600">≈ {(form.duration_ticks * 0.6).toFixed(1)}s</span>
        </label>
        <label className="space-y-1">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">XP / yield</span>
          <input
            type="number" min={0}
            value={form.xp}
            onChange={(e) => setField("xp", Math.max(0, Number(e.target.value) || 0))}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-numeric focus:border-amber-600 outline-none"
            data-testid="admin-action-xp"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">Burn stop lv. (opt)</span>
          <input
            type="number" min={1} max={99}
            value={form.burn_stop_level ?? ""}
            onChange={(e) => setField("burn_stop_level", e.target.value ? Math.max(1, Number(e.target.value)) : null)}
            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-xs font-numeric focus:border-amber-600 outline-none"
            placeholder="—"
            data-testid="admin-action-burn"
          />
        </label>
      </div>

      {/* Yields */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-emerald-500">Yields</span>
          <button
            onClick={() => setField("yields", [...(form.yields || []), { item_id: "", qty: 1, probability: 1.0 }])}
            className="text-[10px] font-body px-2 py-0.5 rounded border border-stone-700 text-stone-400 hover:text-emerald-300 hover:border-emerald-800"
            data-testid="admin-action-yield-add"
          >
            + row
          </button>
        </div>
        {(form.yields || []).map((y, i) => (
          <div key={i} className="grid grid-cols-[1fr_70px_90px_auto] gap-1.5" data-testid={`admin-action-yield-${i}`}>
            <input
              value={y.item_id}
              onChange={(e) => setField("yields", setRow(form.yields, i, { item_id: e.target.value }))}
              placeholder="item_id (e.g. logs)"
              className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-xs font-numeric focus:border-emerald-600 outline-none"
            />
            <input
              type="number" min={1}
              value={y.qty}
              onChange={(e) => setField("yields", setRow(form.yields, i, { qty: Number(e.target.value) || 1 }))}
              className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-xs font-numeric focus:border-emerald-600 outline-none"
            />
            <input
              type="number" step="0.05" min={0} max={1}
              value={y.probability}
              onChange={(e) => setField("yields", setRow(form.yields, i, { probability: Math.max(0, Math.min(1, Number(e.target.value) || 0)) }))}
              className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-xs font-numeric focus:border-emerald-600 outline-none"
            />
            <button
              onClick={() => setField("yields", form.yields.filter((_, idx) => idx !== i))}
              className="text-stone-500 hover:text-red-300 px-1"
              title="Remove row"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      {/* Cost (optional) */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-widest font-numeric text-red-400">Input cost (per cycle)</span>
          <button
            onClick={() => setField("cost", [...(form.cost || []), { item_id: "", qty: 1 }])}
            className="text-[10px] font-body px-2 py-0.5 rounded border border-stone-700 text-stone-400 hover:text-red-300 hover:border-red-800"
            data-testid="admin-action-cost-add"
          >
            + row
          </button>
        </div>
        {(form.cost || []).map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_70px_auto] gap-1.5" data-testid={`admin-action-cost-${i}`}>
            <input
              value={c.item_id}
              onChange={(e) => setField("cost", setRow(form.cost, i, { item_id: e.target.value }))}
              placeholder="item_id"
              className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-xs font-numeric focus:border-red-600 outline-none"
            />
            <input
              type="number" min={1}
              value={c.qty}
              onChange={(e) => setField("cost", setRow(form.cost, i, { qty: Number(e.target.value) || 1 }))}
              className="bg-stone-950 border border-stone-700 rounded px-2 py-1 text-xs font-numeric focus:border-red-600 outline-none"
            />
            <button
              onClick={() => setField("cost", form.cost.filter((_, idx) => idx !== i))}
              className="text-stone-500 hover:text-red-300 px-1"
              title="Remove row"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="text-[11px] font-body px-2.5 py-1 rounded border border-stone-700 text-stone-400 hover:text-stone-200"
        >
          Cancel
        </button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-700 hover:bg-amber-600 text-stone-950 font-body text-xs h-7 px-3"
          data-testid="admin-action-save"
        >
          {saving ? "Saving…" : "Save action"}
        </Button>
      </div>
    </div>
  );
}

function SkillActionsSection({ skill }) {
  const { actions, refresh, loading } = useSkillActions(skill);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const remove = async (a) => {
    const msg = a.is_custom
      ? `Remove custom action "${a.name}"?`
      : `Reset "${a.name}" back to its baseline definition?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/api/admin/actions/${a.action_id}`);
      toast.success(a.is_custom ? "Action removed" : "Action reset to baseline");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="pt-3 border-t border-stone-800/60 space-y-2" data-testid={`skill-actions-${skill}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-numeric text-amber-500">Actions</div>
          <div className="text-[11px] font-body text-stone-500">
            Baseline & custom activities players can train. Changes go live on the next tick.
          </div>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-1 text-[11px] font-body px-2.5 py-1 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30"
          data-testid={`admin-action-new-${skill}`}
        >
          <Plus size={12} /> Add action
        </button>
      </div>

      {creating && (
        <ActionForm
          initial={newActionTemplate(skill)}
          onSaved={() => { setCreating(false); refresh(); }}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <ActionForm
          initial={{ ...editing, is_new: false }}
          onSaved={() => { setEditing(null); refresh(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="grid gap-1.5">
        {loading && !actions.length && (
          <div className="text-stone-500 italic text-xs py-2">Loading…</div>
        )}
        {actions.map((a) => (
          <div
            key={a.action_id}
            className={
              "panel-inset px-3 py-2 flex items-center gap-3 " +
              (a.is_custom ? "border-amber-800/60" : "")
            }
            data-testid={`admin-action-row-${a.action_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-body text-sm text-stone-200 truncate">{a.name}</span>
                <span className="text-stone-600 font-numeric text-[10px]">/{a.action_id}/</span>
                {a.is_custom && (
                  <span className="text-[9px] font-numeric uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-800 text-amber-300">
                    Custom
                  </span>
                )}
              </div>
              <div className="text-[10px] font-numeric text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                <span>Lv {a.level}</span>
                <span>+{a.xp} XP</span>
                <span>{a.seconds}s / cycle</span>
                {a.yields?.length > 0 && (
                  <span>→ {a.yields.map((y) => `${y.qty}× ${y.item_id}${y.probability < 1 ? ` (${Math.round(y.probability*100)}%)` : ""}`).join(", ")}</span>
                )}
                {a.cost?.length > 0 && (
                  <span className="text-red-400">cost {a.cost.map((c) => `${c.qty}× ${c.item_id}`).join(", ")}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => { setEditing(a); setCreating(false); }}
              className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800"
              data-testid={`admin-action-edit-${a.action_id}`}
            >
              Edit
            </button>
            <button
              onClick={() => remove(a)}
              className="text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-500 hover:text-red-400 hover:border-red-900"
              data-testid={`admin-action-delete-${a.action_id}`}
              title={a.is_custom ? "Remove custom action" : "Reset to baseline"}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {!loading && !actions.length && (
          <div className="text-center text-stone-500 italic text-xs py-4">
            No actions defined yet. Add one to give this skill something to train.
          </div>
        )}
      </div>
    </div>
  );
}

function SkillsTab() {
  const { skills, refresh } = useSkillMeta();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter((s) =>
      s.skill.toLowerCase().includes(q) ||
      (s.label || "").toLowerCase().includes(q) ||
      (s.category_label || "").toLowerCase().includes(q)
    );
  }, [skills, query]);

  return (
    <div className="space-y-4" data-testid="admin-skills-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg heading-gilded">Skill metadata</h3>
          <p className="text-[11px] font-body text-stone-500">
            Override the display label, tagline, category name, icon and hero banner
            of any of the 23 skills. Changes go live on the next content refresh.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-stone-950 border border-stone-800 rounded px-2 py-1">
          <Search size={13} className="text-stone-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills…"
            className="bg-transparent outline-none text-xs font-body text-stone-200 placeholder:text-stone-600 w-40"
            data-testid="admin-skills-search"
          />
        </div>
      </div>

      {editing && (
        <SkillForm
          initial={editing}
          onSaved={(updated) => {
            setEditing(updated);
            refresh();
          }}
          onClose={() => setEditing(null)}
        />
      )}

      <div className="grid gap-2">
        {filtered.map((s) => (
          <div
            key={s.skill}
            className="panel-inset p-3 flex items-center gap-3"
            data-testid={`admin-skill-row-${s.skill}`}
          >
            <div className="w-14 h-14 bg-stone-950 border border-stone-800 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
              {s.icon_url ? (
                <img src={absoluteUrl(s.icon_url)} alt={s.label} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={18} className="text-stone-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-body text-sm text-stone-200 truncate flex items-center gap-2">
                {s.label}
                <span className="text-stone-600 font-numeric text-[10px]">/{s.skill}/</span>
                {s.has_override && (
                  <span className="text-[9px] font-numeric uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-800 text-amber-300">
                    Overridden
                  </span>
                )}
              </div>
              {s.tagline && (
                <div className="text-[11px] font-body italic text-stone-500 truncate">{s.tagline}</div>
              )}
              <div className="text-[10px] font-numeric text-stone-600 mt-0.5">
                Category · <span className="text-stone-400">{s.category_label || "—"}</span>
                {s.hero_url && <span className="text-stone-600"> · banner set</span>}
              </div>
            </div>
            <button
              onClick={() => setEditing(s)}
              className="text-[11px] font-body px-2.5 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
              data-testid={`admin-skill-edit-${s.skill}`}
            >
              Edit
            </button>
          </div>
        ))}
        {!filtered.length && (
          <div className="text-center text-stone-500 italic py-10 font-body panel-inset">
            No skills match your search.
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

export function AdminPanel() {
  const { snap } = useGame();
  const isAdmin = !!snap?.is_admin;
  const [tab, setTab] = useState("items");
  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-6xl" data-testid="admin-panel">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-numeric text-amber-500 mb-1">
          <span className={"inline-block w-1.5 h-1.5 rounded-full " + (isAdmin ? "bg-amber-400 animate-pulse" : "bg-stone-600")} />
          Live-ops · {isAdmin ? "Restricted" : "Preview"}
        </div>
        <h2 className="font-display text-3xl heading-gilded">Admin console</h2>
        <p className="text-stone-500 font-body">
          Publish content and swap art without a redeploy. Every upload is stored in Emergent Object Storage.
        </p>
      </div>

      {!isAdmin && (
        <div
          className="panel-inset px-4 py-3 flex items-start gap-3 border-amber-800/60"
          data-testid="admin-preview-banner"
        >
          <Lock size={16} className="text-amber-400 mt-0.5 shrink-0" strokeWidth={1.6} />
          <div className="text-sm font-body text-stone-300">
            <div className="text-amber-300 font-numeric uppercase tracking-widest text-[10px] mb-0.5">Read-only preview</div>
            You can browse the live-ops workbench, but publishing items, uploading assets and changing banners is restricted to designated admins. Contact the team if you need access.
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-900 border border-stone-800 w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="items" className="gap-2" data-testid="admin-tab-items">
            <Package size={14} /> Items
          </TabsTrigger>
          <TabsTrigger value="skills" className="gap-2" data-testid="admin-tab-skills">
            <Sparkles size={14} /> Skills
          </TabsTrigger>
          <TabsTrigger value="banners" className="gap-2" data-testid="admin-tab-banners">
            <Layers size={14} /> Banners
          </TabsTrigger>
          <TabsTrigger value="assets" className="gap-2" data-testid="admin-tab-assets">
            <ImageIcon size={14} /> Assets
          </TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-4"><ItemsTab /></TabsContent>
        <TabsContent value="skills" className="mt-4"><SkillsTab /></TabsContent>
        <TabsContent value="banners" className="mt-4"><BannersTab /></TabsContent>
        <TabsContent value="assets" className="mt-4"><AssetsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
